import { useCallback } from 'react';
import { useToggle, useLatest } from 'react-use';
import { get } from 'lodash';
import {
  useSetAgcGainCapsActionMutation,
  useSetOscMitGainActionMutation,
  useSetAgcDurationActionMutation,
  useSetDefaultCableLossActionMutation
} from 'apis/cm';
import { networkElements } from '../constants';
import { hasNetworkElement, getOscMitGains, getAgcGainCaps, getAgcDuration } from '../utils';

function useGainSettings(
  device,
  {
    onMutate,
    onComplete,
    onSetAgcGainCapsMutate,
    onSetAgcGainCapsSuccess,
    onSetAgcGainCapsError,
    onSetOscMitGainMutate,
    onSetOscMitGainSuccess,
    onSetOscMitGainError,
    onSetAgcDurationMutate,
    onSetAgcDurationSuccess,
    onSetAgcDurationError,
    onSetDefaultCableLossMutate,
    onSetDefaultCableLossSuccess,
    onSetDefaultCableLossError
  } = {}
) {
  const deviceRef = useLatest(device);
  const [open, toggleOpen] = useToggle(false);
  const setAgcGainCapsMutation = useSetAgcGainCapsActionMutation({
    onMutate: onSetAgcGainCapsMutate,
    onSuccess: onSetAgcGainCapsSuccess,
    onError: onSetAgcGainCapsError
  });
  const { mutateAsync: mutateAgcGainCapsAsync } = setAgcGainCapsMutation;
  const setOscMitGainMutation = useSetOscMitGainActionMutation({
    onMutate: onSetOscMitGainMutate,
    onSuccess: onSetOscMitGainSuccess,
    onError: onSetOscMitGainError
  });
  const { mutateAsync: mutateOscMitGainAsync } = setOscMitGainMutation;
  const setAgcDurationMutation = useSetAgcDurationActionMutation({
    onMutate: onSetAgcDurationMutate,
    onSuccess: onSetAgcDurationSuccess,
    onError: onSetAgcDurationError
  });
  const { mutateAsync: mutateAgcDurationAsync } = setAgcDurationMutation;
  const setDefaultCableLossMutation = useSetDefaultCableLossActionMutation({
    onMutate: onSetDefaultCableLossMutate,
    onSuccess: onSetDefaultCableLossSuccess,
    onError: onSetDefaultCableLossError
  });
  const { mutateAsync: mutateDefaultCableLossAsync } = setDefaultCableLossMutation;
  const setGainSettings = useCallback(
    async (nextProps) => {
      const { deviceId } = deviceRef.current;
      const prevProps = get(deviceRef.current, 'properties.reported', {});
      const mutations = [];

      for (let networkElement of networkElements) {
        const agcGainCaps = getAgcGainCaps(networkElement, prevProps, nextProps);
        if (agcGainCaps && Object.keys(agcGainCaps).length > 0) {
          mutations.push([{ 
            deviceId, 
            networkElement, 
            agcGainCaps 
          }, mutateAgcGainCapsAsync]);
        }
      }

      const oscMitGain = getOscMitGains(prevProps, nextProps);
      if (oscMitGain && Object.keys(oscMitGain).length > 0) {
        mutations.push([{ deviceId, oscMitGain }, mutateOscMitGainAsync]);
      }

      // Handle AGC Duration
      const agcDuration = getAgcDuration(prevProps, nextProps);

      if (agcDuration) {
        mutations.push([{ deviceId, agcDuration }, mutateAgcDurationAsync]);
      }

      // Handle Cable Loss - only include changed values
      const changedCableLoss = {};
      let hasCableLossChanges = false;
      let networkElement = null;

      ['rf-path1', 'rf-path2', 'rf-path3'].forEach(path => {
        const nextValue = parseFloat(get(nextProps, `DU.gain-settings.default-cable-loss-${path}-db`));
        const prevValue = parseFloat(get(prevProps, `DU.gain-settings.default-cable-loss-${path}-db`, 0));
        
        // Only include if nextValue exists and is different from prevValue
        if (!isNaN(nextValue) && nextValue !== prevValue) {
          changedCableLoss[`default-cable-loss-${path}-db`] = nextValue;
          hasCableLossChanges = true;
          // Map rf-path to SU number
          networkElement = `SU${path.slice(-1)}`;
        }
      });

      if (hasCableLossChanges) {
        mutations.push([{ 
          deviceId, 
          defaultCableLoss: changedCableLoss,
          force: true,
          networkElement
        }, mutateDefaultCableLossAsync]);
      }

      if (mutations.length === 0) {
        return;
      }

      if (onMutate) {
        onMutate(mutations);
      }

      const results = [];

      for (let [payload, mutateAsync] of mutations) {
        try {
          const data = await mutateAsync(payload);
          results.push([null, data]);
        } catch (error) {
          results.push([error, null]);
        }
      }

      if (onComplete) {
        onComplete(results, mutations);
      }
    },
    [deviceRef, mutateAgcGainCapsAsync, mutateOscMitGainAsync, mutateAgcDurationAsync, mutateDefaultCableLossAsync, onMutate, onComplete]
  );
  const properties = get(device, 'properties.reported');

  return {
    setAgcGainCapsMutation,
    setOscMitGainMutation,
    setAgcDurationMutation,
    setDefaultCableLossMutation,
    trigger: {
      onClick: toggleOpen
    },
    tooltip: {},
    dialog: {
      properties,
      open,
      su1: hasNetworkElement(properties, 'SU1'),
      su2: hasNetworkElement(properties, 'SU2'),
      su3: hasNetworkElement(properties, 'SU3'),
      su4: hasNetworkElement(properties, 'SU4'),
      onClose: toggleOpen,
      onSubmit: setGainSettings
    }
  };
}

export default useGainSettings;
